import React, { useEffect, useMemo, useState } from 'react';
import { DataGridPro } from '@mui/x-data-grid-pro';
import { nanoid } from 'nanoid';

import { usePutAcquisitionsMutation } from '../../../store/slices/apiSlice/acquisitions-api-slice.js';
import { datagridsx } from '../../../constants/datagridsx.js';
import { getTotalDaysByCity } from '../../../utilities/acquisitionsHelpers.js';
import TableButton from '../components/acquisitionsTableButton.js';
import AddSaleTargetButton from '../components/addSaleTargetButton.js';
import { handleBackendError } from '../../../utilities/errorHandling.js';
import { toastSuccess } from '../../../utilities/toast.js';
import {
    getPacificDateFromInput,
    formatDateMMDDYYPacific,
    getPacificDateFromInputWithDifference,
} from '../../../utilities/dateHelpers.js';

const TargetDateColumnWidth = 140;
const AssumptionColumnWidth = 200;

const calculateTableRow = (tgtDate, cities, totalDaysByCity, id = nanoid()) => {
    const row = { date: new Date(tgtDate), id };
    for (const city of cities) {
        const subtracted = getPacificDateFromInputWithDifference(
            tgtDate,
            totalDaysByCity[city]
        );
        row[city] = subtracted;
    }
    return row;
};

const initializeTableRows = (targets, cities, totalDaysByCity) => {
    return targets.map((tgt) => calculateTableRow(tgt, cities, totalDaysByCity));
};

const AcquisitionsSalesTargets = ({ targets, assumptions }) => {
    const [updateAcquisitions, updateAcquisitionsResponse] =
    usePutAcquisitionsMutation();
    const [dirty, setDirty] = useState(false);
    const totalDaysByCity = useMemo(
        () => getTotalDaysByCity(assumptions),
        [assumptions]
    );
    const cities = useMemo(() => assumptions.map((a) => a.label), [assumptions]);

    const [tableRows, setTableRows] = useState(() =>
        initializeTableRows(targets, cities, totalDaysByCity)
    );

    const columnDefinitions = useMemo(() => {
        const cols = [
            {
                field: 'date',
                headerName: 'Target Date',
                type: 'date',
                editable: true,
                width: TargetDateColumnWidth,
                headerAlign: 'center',
                align: 'center',
                renderCell: (params) => (
                    <span>{formatDateMMDDYYPacific(params.value)}</span>
                ),              
            },
        ];
        assumptions.forEach((assumption) =>
            cols.push({
                field: assumption.label,
                headerName: assumption.label,
                renderHeader: (params) => {
                    if (!params.colDef) {
                        return null;
                    }
                    return (
                        <div
                            className="MuiDataGrid-columnHeaderTitle"
                            style={{ textAlign: 'center' }}
                        >
                            {params.colDef.headerName}
                            <br />
              Minimum Offer Accepted
                        </div>
                    );
                },
                type: 'date',
                width: AssumptionColumnWidth,
                headerAlign: 'center',
                align: 'center',
                renderCell: (params) => (
                    <span>{formatDateMMDDYYPacific(params.value)}</span>
                ),
            })
        );
        return cols;
    }, [assumptions]);

    const targetDates = useMemo(() => tableRows.map((r) => r.date), [tableRows]);

    useEffect(() => {
        if (updateAcquisitionsResponse) {
            const { isError, status, isSuccess } = updateAcquisitionsResponse;
            if (isError) {
                handleBackendError(
                    'Update Acquisitions Error',
                    updateAcquisitionsResponse
                );
            } else if (status === 'fulfilled' && isSuccess) {
                toastSuccess('Acquisitions Updated');
            }
        }
    }, [updateAcquisitionsResponse]);

    const handleSave = () => {
        const updatedTargets = tableRows.map((tr) => tr.date.toISOString());
        updateAcquisitions({ targets: [...updatedTargets] });
        setDirty(false);
    };

    const handleReset = () => {
        setTableRows(initializeTableRows(targets, cities, totalDaysByCity));
        setDirty(false);
    };

    const handleAddTargetDate = (newDate) => {
        const newRow = { id: nanoid(), date: getPacificDateFromInput(newDate) };
        for (const city of cities) {
            const subtracted = getPacificDateFromInputWithDifference(
                newDate,
                totalDaysByCity[city]
            );
            newRow[city] = subtracted;
        }
        const newRows = [...tableRows];
        newRows.push(newRow);
        setTableRows(newRows);
        setDirty(true);
    };

    const containerWidth = `${
        assumptions.length * AssumptionColumnWidth + TargetDateColumnWidth + 20
    }px`;
    return (
        <div
            className="acquisitions-salestargets-container"
            style={{ '--salestargets-container-width': containerWidth }}
        >
            <div className="w-100 d-flex justify-content-end">
                <AddSaleTargetButton
                    onAdd={handleAddTargetDate}
                    targetDates={targetDates}
                />
            </div>
            <DataGridPro
                rows={tableRows}
                columns={columnDefinitions}
                disableColumnSorting
                disableColumnMenu={true}
                editMode="cell"
                initialState={{
                    sorting: {
                        sortModel: [{ field: 'date', sort: 'asc' }],
                    },
                }}
                processRowUpdate={(newRow, oldRow) => {
                    if (JSON.stringify(newRow) !== JSON.stringify(oldRow)) {
                        setDirty(true);
                        const recalculatedRow = calculateTableRow(
                            newRow.date.toISOString(),
                            cities,
                            totalDaysByCity,
                            newRow.id
                        );
                        setTableRows((prev) =>
                            prev.map((r) =>
                                r.id === recalculatedRow.id ? recalculatedRow : r
                            )
                        );
                        return recalculatedRow;
                    }
                    return newRow;
                }}
                experimentalFeatures={{ newEditingApi: true }}
                sx={{
                    ...datagridsx,
                    '& .MuiDataGrid-footerContainer': {
                        display: 'none',
                    },
                }}
            />
            <div className="mt-4 w-100 d-flex justify-content-center align-items-center gap-4">
                <TableButton label="Save" disabled={!dirty} onClick={handleSave} />
                <TableButton label="Reset" disabled={!dirty} onClick={handleReset} />
            </div>
        </div>
    );
};

export default AcquisitionsSalesTargets;
